/**
 * CM intro email (0161) — template seam + renderer for the People-tab
 * "Copy intro email" block.
 *
 * The block the unit lead copies into Outlook when welcoming a new client:
 * a rich-text email body assembled from the CM's profile (name, credentials,
 * photo, bio, email, phone) and signed by the signed-in unit lead. Fields
 * the unit lead must fill by hand in Outlook ([Client first name],
 * [Timing note], and any profile fact not on file) render as VISUALLY
 * OBVIOUS highlighted placeholders — in the on-page preview AND in the
 * copied HTML — so a half-filled email never goes out unnoticed.
 *
 * COMMERCIALIZATION D2 config seam (readRateBands / readQuietDaysThreshold
 * precedent): the paragraph templates read from
 * `config.features.cm_intro_email` (`{ paragraphs: string[] }`), falling
 * back to INTRO_EMAIL_PARAGRAPHS when unset/malformed. Peace sets no config
 * row — the fallback IS the tenant wording. The copy is tenant-neutral
 * (no staff or company names; the practice is "our practice").
 *
 * Wording note (founder ruling 2026-07-25, PR #614 review): the outreach
 * paragraph keeps the founder's original sample phrasing verbatim —
 * "introduce herself ... her initial meeting" — NOT neutral pronouns; a
 * tenant whose voice differs overrides via the config seam. The spaced-
 * hyphen rewrite in the closing paragraph ("our practice. We look
 * forward...") was ratified in the same review.
 *
 * Pure module (no React, no fetch) so template assembly is unit-testable;
 * the clipboard write + photo-fetch live in the component.
 */

import { roleNoun } from '@/lib/people';

// ---------------------------------------------------------------------
// Template + seam
// ---------------------------------------------------------------------

/**
 * Tokens available to paragraph templates:
 *   {client_first_name}         always a highlighted [Client first name]
 *                               placeholder (the unit lead fills it)
 *   {timing_note}               always a highlighted [Timing note] placeholder
 *   {cm_name_with_credentials}  "Jane Doe, BSW, CSW," or "Jane Doe" when no
 *                               credentials on file (getReportSignatory
 *                               precedent: null omits the credential clause)
 *   {cm_first_name}             preferred_name ?? first token of full_name
 *                               (getValedictionName precedent)
 *   {cm_role}                   what the person is to the family: the role
 *                               noun from their capability flags (roleNoun),
 *                               "care manager" when the caller supplies none
 *   {photo}                     a paragraph of its own: the CM photo inlined
 *                               as a data URI <img>; omitted when no photo
 *   {cm_bio}                    a paragraph of its own: the CM bio with
 *                               paragraph breaks preserved; a highlighted
 *                               [Bio] placeholder when none on file
 *   {cm_email}                  roster email, or a highlighted [Email]
 *                               placeholder when none on file
 *   {cm_phone}                  direct phone, or a highlighted [Phone]
 *                               placeholder when none on file
 *   {sender_name}               the signed-in unit lead's display name, or a
 *                               highlighted [Your name] placeholder
 */
export const INTRO_EMAIL_PARAGRAPHS: readonly string[] = [
  'Dear {client_first_name},',
  "We are so glad you've chosen to work with our care management team! We are ready to get started and begin the work that will help you ensure that you have the right support around you.",
  "{cm_name_with_credentials} will be the {cm_role} working directly with you. Here's a little information about {cm_first_name}:",
  '{photo}',
  '{cm_bio}',
  '{cm_first_name} will be reaching out to you very shortly to introduce herself and talk to you about next steps and to set up a date/time for her initial meeting with you. {timing_note}',
  "{cm_first_name}'s direct contact information is below:\nEmail. {cm_email}\nPhone. {cm_phone}",
  // Writing-guide note: the founder's sample joined these clauses with a
  // spaced hyphen ("our practice - we look forward"), which the brand lint
  // bans in copy; a period is the guide-sanctioned substitute (flagged in
  // the PR for founder review).
  '{client_first_name}, once again, we would like to welcome you to our practice. We look forward to working with you!',
  'My very best,\n{sender_name}',
];

/**
 * Read the tenant intro-email paragraph templates from tenants.config
 * (`config.features.cm_intro_email`: `{ paragraphs: string[] }` with
 * non-empty strings), falling back to INTRO_EMAIL_PARAGRAPHS when unset/
 * malformed/empty (D2 config seam; readRateBands defensive-walk precedent —
 * only well-formed entries are kept, never a blank email). Pure; unit-tested.
 */
export function readIntroEmailParagraphs(config: unknown): readonly string[] {
  if (typeof config !== 'object' || config === null) return INTRO_EMAIL_PARAGRAPHS;
  const features = (config as Record<string, unknown>).features;
  if (typeof features !== 'object' || features === null) return INTRO_EMAIL_PARAGRAPHS;
  const v = (features as Record<string, unknown>).cm_intro_email;
  if (typeof v !== 'object' || v === null) return INTRO_EMAIL_PARAGRAPHS;
  const paragraphs = (v as Record<string, unknown>).paragraphs;
  if (!Array.isArray(paragraphs)) return INTRO_EMAIL_PARAGRAPHS;
  const kept: string[] = [];
  for (const p of paragraphs) {
    if (typeof p === 'string' && p.trim().length > 0) kept.push(p);
  }
  return kept.length > 0 ? kept : INTRO_EMAIL_PARAGRAPHS;
}

// ---------------------------------------------------------------------
// Renderer
// ---------------------------------------------------------------------

/** The profile facts the template renders from. Any null renders a
 *  highlighted placeholder (photo excepted — an absent photo is omitted). */
export interface IntroEmailData {
  full_name: string;
  credentials: string | null;
  preferred_name: string | null;
  bio: string | null;
  email: string | null;
  phone: string | null;
  /** The already-loaded CM photo as a data URI (Outlook inlines it on
   *  paste). null = no photo → the photo paragraph is omitted entirely. */
  photo_data_uri: string | null;
  /** The signed-in unit lead's display name (whoami.display_name). */
  sender_name: string | null;
  /** What the template calls the person: roleNoun over their capability
   *  flags ("care manager", "SPICE therapist", both, or "team member"). A
   *  caller that already knows the person is the client's care manager
   *  (the work-item drawer's primary-CM row) may omit it; the template
   *  then says "care manager". */
  role_noun?: string;
}

export interface IntroEmailRender {
  /** Rich HTML for the clipboard's text/html flavor + the on-page preview. */
  html: string;
  /** Plain-text fallback (text/plain flavor); placeholders keep their
   *  bracket form, the photo is omitted. */
  text: string;
}

/** First-name derivation — the getValedictionName precedent:
 *  preferred_name verbatim when set, else the first token of full_name. */
export function deriveFirstName(preferredName: string | null, fullName: string): string {
  const preferred = (preferredName ?? '').trim();
  if (preferred.length > 0) return preferred;
  return (fullName ?? '').trim().split(/\s+/)[0] ?? '';
}

/** Inline style for fill-me-in placeholders. Inline (not a class) so the
 *  highlight survives the clipboard round-trip into Outlook. */
const PLACEHOLDER_STYLE =
  'background-color:#fde047;color:#1f2937;font-weight:600;padding:0 2px;border-radius:2px;';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** One rendered token: the HTML flavor + the plain-text flavor. */
interface Piece {
  html: string;
  text: string;
}

function placeholderPiece(label: string): Piece {
  return {
    html: `<span style="${PLACEHOLDER_STYLE}">${escapeHtml(label)}</span>`,
    text: label,
  };
}

function valuePiece(value: string): Piece {
  return { html: escapeHtml(value), text: value };
}

/** A profile fact or, when not on file, its highlighted placeholder. */
function factPiece(value: string | null, placeholderLabel: string): Piece {
  const trimmed = (value ?? '').trim();
  return trimmed.length > 0 ? valuePiece(trimmed) : placeholderPiece(placeholderLabel);
}

const TOKEN_RE = /\{(client_first_name|timing_note|cm_name_with_credentials|cm_first_name|cm_role|photo|cm_bio|cm_email|cm_phone|sender_name)\}/g;

/** The noun for a caller that supplies none: the person is the client's
 *  care manager by construction there. */
const DEFAULT_ROLE_NOUN = roleNoun({ does_care_management: true, does_spice: false });

/**
 * Render the intro email as { html, text } from the paragraph templates +
 * profile facts. Every interpolated value is HTML-escaped (the bio is
 * operator-entered free text; the preview renders this HTML directly).
 */
export function renderIntroEmail(
  data: IntroEmailData,
  paragraphs: readonly string[] = INTRO_EMAIL_PARAGRAPHS,
): IntroEmailRender {
  const firstName = deriveFirstName(data.preferred_name, data.full_name);
  const credentials = (data.credentials ?? '').trim();
  const fullName = (data.full_name ?? '').trim();
  // "Jane Doe, BSW, CSW," reads as an appositive; without credentials the
  // clause (and its commas) is omitted: "Jane Doe".
  const nameWithCredentials =
    credentials.length > 0 ? `${fullName}, ${credentials},` : fullName;

  const pieces: Record<string, Piece> = {
    client_first_name: placeholderPiece('[Client first name]'),
    timing_note: placeholderPiece('[Timing note]'),
    cm_name_with_credentials: valuePiece(nameWithCredentials),
    cm_first_name: valuePiece(firstName),
    cm_role: valuePiece(data.role_noun ?? DEFAULT_ROLE_NOUN),
    cm_email: factPiece(data.email, '[Email]'),
    cm_phone: factPiece(data.phone, '[Phone]'),
    sender_name: factPiece(data.sender_name, '[Your name]'),
  };

  const htmlParts: string[] = [];
  const textParts: string[] = [];

  for (const template of paragraphs) {
    // {photo} on a paragraph of its own: the inlined image, or nothing.
    if (template.trim() === '{photo}') {
      if (data.photo_data_uri) {
        htmlParts.push(
          `<p><img src="${data.photo_data_uri}" alt="${escapeHtml(`Photo of ${fullName}`)}" width="180" style="max-width:180px;border-radius:6px;" /></p>`,
        );
      }
      continue; // never in the plain-text flavor
    }

    // {cm_bio} on a paragraph of its own: bio paragraphs preserved, or a
    // highlighted [Bio] placeholder when none on file.
    if (template.trim() === '{cm_bio}') {
      const bio = (data.bio ?? '').trim();
      if (bio.length > 0) {
        const bioParagraphs = bio.split(/\n\s*\n/);
        for (const p of bioParagraphs) {
          htmlParts.push(`<p>${escapeHtml(p.trim()).replace(/\n/g, '<br />')}</p>`);
          textParts.push(p.trim());
        }
      } else {
        const ph = placeholderPiece('[Bio]');
        htmlParts.push(`<p>${ph.html}</p>`);
        textParts.push(ph.text);
      }
      continue;
    }

    // Ordinary paragraph: substitute tokens, escape everything else.
    let html = '';
    let text = '';
    let last = 0;
    TOKEN_RE.lastIndex = 0;
    for (let m = TOKEN_RE.exec(template); m !== null; m = TOKEN_RE.exec(template)) {
      const literal = template.slice(last, m.index);
      html += escapeHtml(literal);
      text += literal;
      const piece = pieces[m[1]!] ?? valuePiece(m[0]);
      html += piece.html;
      text += piece.text;
      last = m.index + m[0].length;
    }
    const tail = template.slice(last);
    html += escapeHtml(tail);
    text += tail;

    htmlParts.push(`<p>${html.replace(/\n/g, '<br />')}</p>`);
    textParts.push(text);
  }

  return {
    // Calibri 11pt matches Outlook's default body style so the pasted block
    // does not look foreign next to the unit lead's own typing.
    html: `<div style="font-family:Calibri, Arial, sans-serif;font-size:11pt;line-height:1.4;">${htmlParts.join('')}</div>`,
    text: textParts.join('\n\n'),
  };
}
